// src/pages/msme/SigmaStudioKRA.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faEdit,
  faEye,
  faFilePdf,
  faDatabase,
  faTimes,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import jsPDF from "jspdf";
import "jspdf-autotable";

const exampleKRAs = [
  {
    id: "KRA-01",
    objective: "Achieve 98% on-time delivery of finished adhesive labels",
    actionPlan: "Implement daily production tracking + buffer stock for high-demand SKUs",
    kpi: "On-time delivery % (target ≥98%)",
    completionDate: "2025-12-31",
    reviewPeriod: "Quarterly",
    weightage: 10,
    remarks: "Critical for customer satisfaction",
  },
  {
    id: "KRA-02",
    objective: "Reduce material wastage in printing & die-cutting by 15%",
    actionPlan: "Standardize setup procedures, install vision inspection system",
    kpi: "Wastage % (from current 8.2% → target ≤7%)",
    completionDate: "2025-09-30",
    reviewPeriod: "Monthly",
    weightage: 9,
    remarks: "",
  },
  {
    id: "KRA-03",
    objective: "Increase production output by 20% without additional manpower",
    actionPlan: "Reduce changeover time from 45 min to 25 min avg",
    kpi: "Labels produced per shift (target +20%)",
    completionDate: "2025-10-15",
    reviewPeriod: "Quarterly",
    weightage: 10,
    remarks: "SMED implementation planned",
  },
  {
    id: "KRA-04",
    objective: "Achieve zero customer complaints related to label adhesion failure",
    actionPlan: "Supplier audit + incoming adhesive quality testing protocol",
    kpi: "Number of adhesion-related complaints (target = 0)",
    completionDate: "2025-12-31",
    reviewPeriod: "Monthly",
    weightage: 8,
    remarks: "High impact on repeat business",
  },
  {
    id: "KRA-05",
    objective: "Maintain 100% compliance with BIS & FDA labeling regulations",
    actionPlan: "Quarterly mock audits + training for packaging team",
    kpi: "% compliance in internal audits",
    completionDate: "Ongoing",
    reviewPeriod: "Quarterly",
    weightage: 7,
    remarks: "Statutory requirement",
  },
];

const initialKRA = {
  id: "",
  objective: "",
  actionPlan: "",
  kpi: "",
  completionDate: "",
  reviewPeriod: "",
  weightage: "",
  remarks: "",
};

const SigmaStudioKRA = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const [header, setHeader] = useState({
    personName: "",
    designation: "",
    date: new Date().toISOString().split("T")[0],
    version: "1.0",
    remarks: "",
  });

  const [kras, setKras] = useState([]);
  const [form, setForm] = useState(initialKRA);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showTableView, setShowTableView] = useState(false);

  const handleHeaderChange = (e) => {
    setHeader({ ...header, [e.target.name]: e.target.value });
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const addOrUpdateKRA = (e) => {
    e.preventDefault();
    if (editingIndex !== null) {
      const updated = [...kras];
      updated[editingIndex] = { ...form };
      setKras(updated);
      setEditingIndex(null);
    } else {
      setKras([...kras, { ...form, id: form.id || `KRA-${String(kras.length + 1).padStart(2, "0")}` }]);
    }
    setForm(initialKRA);
  };

  const editKRA = (index) => {
    setForm(kras[index]);
    setEditingIndex(index);
  };

  const deleteKRA = (index) => {
    if (window.confirm("Delete this KRA?")) {
      setKras(kras.filter((_, i) => i !== index));
    }
  };

  const personNameRef = React.useRef(null);

  // In populateExample, after setHeader():
  setTimeout(() => {
    personNameRef.current?.focus();
    personNameRef.current?.select();
  }, 100);

  const populateExample = () => {
    setHeader({
      personName: "Sunil Sawant",
      designation: "Production & Operations Head",
      date: new Date().toISOString().split("T")[0], // keeps today's date
      version: "1.0",
      remarks: "FY 2025-26 – Annual Performance Goals (Adhesive Label Manufacturing Unit)",
    });
    setKras([...exampleKRAs]);
    setShowTableView(true);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.text("Key Result Areas (KRA) / Goal Sheet", 14, 20);

    doc.setFontSize(11);
    doc.text(`Person: ${header.personName || "—"}`, 14, 32);
    doc.text(`Designation: ${header.designation || "—"}`, 14, 38);
    doc.text(`Date: ${header.date || "—"}`, 14, 44);
    doc.text(`Version: ${header.version || "—"}`, 14, 50);
    if (header.remarks) {
      doc.text(`Remarks: ${header.remarks}`, 14, 56);
    }

    const tableColumn = [
      "SN",
      "KRA ID",
      "Objective",
      "Action Plan",
      "KPI / Parameters",
      "Tentative Completion",
      "Review Period",
      "Weightage",
      "Remarks",
    ];

    const tableRows = kras.map((kra, idx) => [
      idx + 1,
      kra.id,
      kra.objective,
      kra.actionPlan,
      kra.kpi,
      kra.completionDate,
      kra.reviewPeriod,
      kra.weightage,
      kra.remarks || "",
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: header.remarks ? 65 : 58,
      styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak" },
      headStyles: { fillColor: [44, 62, 80], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 8 },
        1: { cellWidth: 18 },
        2: { cellWidth: 38 },
        3: { cellWidth: 38 },
        4: { cellWidth: 32 },
        5: { cellWidth: 22 },
        6: { cellWidth: 18 },
        7: { cellWidth: 14 },
        8: { cellWidth: 22 },
      },
    });

    doc.save(`KRA_${header.personName || "Untitled"}_${header.date || "date"}.pdf`);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-6">
      <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-yellow-200">
            🎛️ Think5Whys Sigma Studio
          </div>
          <div className="text-[11px] text-gray-200">
            KRA Playground
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs lg:text-sm text-teal-300">
                {userName}
              </div>

              <button
                onClick={handleLogout}
                className="w-8 mt-2 px-2 py-1 rounded-md bg-red-600 hover:bg-red-700"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-8 px-2 py-1 rounded-full border border-gray-700 text-sm"
            >
              🔐
            </button>
          )}
        </div>

        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-8 h-8 flex items-center justify-center text-2xl text-teal-300"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-3">

              <div className="text-teal-300 text-xs lg:text-sm">
                {userName}
              </div>

              <button
                onClick={handleLogout}
                className="p-2 rounded-md bg-red-600"
              >
                ⏻
              </button>

            </div>
          ) : (
            <div className="flex justify-center">
              <Link to="/login" className="text-teal-300">
                🔐
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Header Form */}
      <div className="bg-gray-800 p-5 rounded-lg mb-6 border border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm mb-1">Person Name</label>
            <input
              ref={personNameRef}
              name="personName"
              value={header.personName}
              onChange={handleHeaderChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
              placeholder="e.g. Sunil Sawant"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Designation</label>
            <input
              name="designation"
              value={header.designation}
              onChange={handleHeaderChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
              placeholder="e.g. Production Head"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Date</label>
            <input
              type="date"
              name="date"
              value={header.date}
              onChange={handleHeaderChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Version</label>
            <input
              name="version"
              value={header.version}
              onChange={handleHeaderChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Remarks</label>
            <input
              name="remarks"
              value={header.remarks}
              onChange={handleHeaderChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
              placeholder="Appraisal period / FY..."
            />
          </div>
        </div>
      </div>

      {/* KRA Input Form */}
      <div className="bg-gray-800 p-5 rounded-lg mb-6 border border-gray-700">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg text-yellow-200 font-semibold">
            {editingIndex !== null ? "Edit KRA" : "Add New KRA"}
          </h2>
          <button
            onClick={populateExample}
            className="w-64 px-4 py-2 text-white bg-purple-600 hover:bg-purple-700 rounded text-sm flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faDownload} /> <span className="text-white">Populate from Example</span>
          </button>
        </div>

        <form onSubmit={addOrUpdateKRA} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-1">KRA ID</label>
            <input
              name="id"
              value={form.id}
              onChange={handleFormChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
              placeholder="KRA-01 (auto if empty)"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm mb-1">Objective for KRA</label>
            <input
              name="objective"
              value={form.objective}
              onChange={handleFormChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm mb-1">Action Plan</label>
            <textarea
              name="actionPlan"
              value={form.actionPlan}
              onChange={handleFormChange}
              style={{width: "100%", height: "200px"}}
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm mb-1">Performance Parameters / KPI</label>
            <input
              name="kpi"
              value={form.kpi}
              onChange={handleFormChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm mb-1">Tentative Date of Completion</label>
            <input
              type="date"
              name="completionDate"
              value={form.completionDate}
              onChange={handleFormChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm mb-1">Review Period</label>
            <select
              name="reviewPeriod"
              value={form.reviewPeriod}
              onChange={handleFormChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
            >
              <option value="">Select...</option>
              <option value="Monthly">Monthly</option>
              <option value="Quarterly">Quarterly</option>
              <option value="Half-yearly">Half-yearly</option>
              <option value="Annual">Annual</option>
            </select>
          </div>

          <div>
            <label className="block text-sm mb-1">Weightage (1-10)</label>
            <input
              type="number"
              name="weightage"
              min="1"
              max="10"
              value={form.weightage}
              onChange={handleFormChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm mb-1">Remarks</label>
            <input
              name="remarks"
              value={form.remarks}
              onChange={handleFormChange}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2"
            />
          </div>

          <div className="md:col-span-2 flex justify-end gap-3 mt-2">
            {editingIndex !== null && (
              <button
                type="button"
                onClick={() => {
                  setForm(initialKRA);
                  setEditingIndex(null);
                }}
                className="w-32 px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="w-40 px-5 py-2 bg-green-600 hover:bg-green-700 rounded flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              {editingIndex !== null ? "Update KRA" : "Add KRA"}
            </button>
          </div>
        </form>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 place-items-center mb-6">
        <button
          onClick={() => setShowTableView(!showTableView)}
          className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 rounded flex items-center gap-2"
          disabled={kras.length === 0}
        >
          <FontAwesomeIcon icon={faEye} />
          {showTableView ? "Hide" : "View"} KRA Table
        </button>

        <button
          onClick={exportToPDF}
          className="px-5 py-2 bg-red-600 hover:bg-red-700 rounded flex items-center gap-2"
          disabled={kras.length === 0}
        >
          <FontAwesomeIcon icon={faFilePdf} /> Save as PDF
        </button>

        <button
          disabled
          title="Database integration coming soon"
          className="px-5 py-2 bg-gray-600 rounded flex items-center gap-2 opacity-60 cursor-not-allowed"
        >
          <FontAwesomeIcon icon={faDatabase} /> Save to DB
        </button>
      </div>

      {/* List / Table View */}
      {kras.length > 0 && (
        <div>
          {showTableView ? (
            <div className="overflow-x-auto bg-gray-800 rounded border border-gray-700">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left">SN</th>
                    <th className="px-4 py-2 text-left">KRA ID</th>
                    <th className="px-4 py-2 text-left">Objective</th>
                    <th className="px-4 py-2 text-left">KPI</th>
                    <th className="px-4 py-2 text-center">Weightage</th>
                    <th className="px-4 py-2 text-center">Completion</th>
                    <th className="px-4 py-2 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {kras.map((kra, idx) => (
                    <tr key={idx} className="border-t border-gray-700 hover:bg-gray-700">
                      <td className="px-4 py-3">{idx + 1}</td>
                      <td className="px-4 py-3">{kra.id}</td>
                      <td className="px-4 py-3">{kra.objective}</td>
                      <td className="px-4 py-3">{kra.kpi}</td>
                      <td className="px-4 py-3 text-center font-bold">{kra.weightage}</td>
                      <td className="px-4 py-3 text-center">{kra.completionDate || "—"}</td>
                      <td className="px-4 py-3 flex justify-center gap-2">
                        <button
                          onClick={() => editKRA(idx)}
                          className="p-1.5 bg-yellow-600 hover:bg-yellow-700 rounded"
                        >
                          <FontAwesomeIcon icon={faEdit} size="sm" />
                        </button>
                        <button
                          onClick={() => deleteKRA(idx)}
                          className="p-1.5 bg-red-600 hover:bg-red-700 rounded"
                        >
                          <FontAwesomeIcon icon={faTrash} size="sm" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="space-y-4">
              {kras.map((kra, idx) => (
                <div
                  key={idx}
                  className="bg-gray-800 p-4 rounded border border-gray-700"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-bold">{kra.id} • {kra.objective}</div>
                      <div className="text-sm text-gray-400 mt-1">
                        Weightage: <span className="font-semibold text-white">{kra.weightage}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => editKRA(idx)}
                        className="p-2 bg-yellow-600 hover:bg-yellow-700 rounded"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        onClick={() => deleteKRA(idx)}
                        className="p-2 bg-red-600 hover:bg-red-700 rounded"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mt-3">
                    <div>
                      <span className="text-gray-400">Action Plan:</span><br />
                      {kra.actionPlan}
                    </div>
                    <div>
                      <span className="text-gray-400">KPI:</span><br />
                      {kra.kpi}
                    </div>
                    <div>
                      <span className="text-gray-400">Completion:</span><br />
                      {kra.completionDate || "—"}
                    </div>
                    <div>
                      <span className="text-gray-400">Review:</span><br />
                      {kra.reviewPeriod || "—"}
                    </div>
                    {kra.remarks && (
                      <div className="sm:col-span-2">
                        <span className="text-gray-400">Remarks:</span><br />
                        {kra.remarks}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {kras.length === 0 && (
        <div className="text-center text-gray-400 py-10">
          No KRAs added yet. Click "Add KRA" or "Populate from Example"
        </div>
      )}
      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SigmaStudioKRA;